import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import type { CommonInfo } from "../types/cms/common";

const currencySymbols: Record<string, string> = {
  USD: "$",
  CLP: "$",
  ARS: "$",
  PEN: "S/",
  BRL: "R$",
  COP: "$",
  UYU: "$U",
  PYG: "₲",
  EUR: "€",
  GBP: "£",
};
export const noDecimalCurrencies = ["CLP", "ARS", "PYG", "COP"];

/**
 * Rounds an amount to the smallest unit the currency is priced in: whole units for
 * no-decimal currencies, cents otherwise. Matches the BFF's baggage fee rounding.
 */
export const roundToCurrencyUnit = (value: number, currency: string): number =>
  noDecimalCurrencies.includes(currency)
    ? Math.round(value)
    : Math.round((value + Number.EPSILON) * 100) / 100;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getCurrencySymbol = (currencyCode?: string) => {
  if (!currencyCode) {
    return "";
  }

  return currencySymbols[currencyCode.toUpperCase()] ?? currencyCode.toUpperCase();
};

export const getNumberSeparators = (
  locale: string = "en-US",
): { thousandSeparator: string; decimalSeparator: string } => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89); //Hardcoded number to ensure both thousand and decimal separators are included and the seperators are correctly identified based on the locale and returned.

  return {
    thousandSeparator: parts.find((part) => part.type === "group")?.value ?? ",",
    decimalSeparator: parts.find((part) => part.type === "decimal")?.value ?? ".",
  };
};

export const formatCurrency = (
  value: number,
  currency: string,
  locale: string = "en-US",
): string => {
  if (!currency) {
    console.warn("[formatCurrency] No currency provided, defaulting to USD");
    currency = "USD";
  }
  const isNoDecimalCurrency = noDecimalCurrencies.includes(currency);

  const fractionDigits = isNoDecimalCurrency ? 0 : 2;

  const minimumFractionDigits = fractionDigits;

  const maximumFractionDigits = fractionDigits;

  //  Override locale for Ecuador with USD
  const effectiveLocale = locale === "es-EC" && currency === "USD" ? "en-US" : locale;

  const formatted = new Intl.NumberFormat(effectiveLocale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
  // return formatted.replace(/^[^\d]+/, currencySymbols[currency] + "")
  return formatted.replace(
    /^(-?)(\s?)[^\d-]+/, // capture optional "-" and optional space first
    `$1$2${currencySymbols[currency] ?? ""}`, // reinsert them, then your custom symbol
  );
};

export function removeAccents(str: string) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * @name formatDateTime
 * @description Formats a date string into a custom string based on the provided format.
 * The function replaces tokens in the format string with corresponding date and time values.
 * Supported tokens are:
 * - DDD: Short day name (e.g., Wed)
 * - DD: Day of the month (e.g., 01)
 * - MMM: Short month name (e.g., Jan)
 * - MM: Month number (e.g., 01)
 * - YY: Two-digit year (e.g., 25)
 * - YYYY: Four-digit year (e.g., 2025)
 * - HH: Hours (24-hour format, e.g., 14)
 * - hh: Hours (12-hour format, e.g., 08)
 * - mm: Minutes (e.g., 05)
 * - ss: Seconds (e.g., 08)
 * - A: AM/PM (e.g., AM)
 * - a: am/pm (e.g., am)
 *
 * @param {string | Date} dateInput The date string or Date object to format. Accepts various formats, including "YYYY-MM-DD", "YYYY-MM-DDTHH:mm", or Date objects.
 * @param {string} format The format string with tokens to be replaced.
 * @param {boolean} utc Use utc date to be replaced.
 * @returns {string} The formatted date and time string, or an empty string if input is invalid.
 *
 * @example
 * formatDateTime("2025-06-27", "DDD MMM DD, YYYY"); // "Fri Jun 27, 2025"
 * formatDateTime("2025-06-27", "DD MMM. YY"); // "27 Jun. 25"
 * formatDateTime("2025-09-10T14:30", "HH:mm"); // "14:30"
 * formatDateTime("2025-09-10", "DD-MMM-YY"); // "10-Sep-25"
 * formatDateTime("2025-09-10T14:30", "DD/MM/YYYY HH:mm"); // "10/09/2025 14:30"
 * formatDateTime("2025-09-10T14:30", "DD/MM/YYYY hh:mm a"); // "10/09/2025 02:30 pm"
 * formatDateTime(new Date("2025-06-27"), "DDD MMM DD, YYYY"); // "Fri Jun 27, 2025"
 * formatDateTime(selected.toUTCString(), "MMM DD-MM", true); // "Jan 22-01"
 */
export const formatDateTime = (
  dateInput?: string | Date,
  format?: string,
  utc: boolean = false,
): string => {
  if (!dateInput || !format) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

  const pad = (n: number) => n.toString().padStart(2, "0");

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ] as const;

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const ampm = hours24 >= 12 ? "PM" : "AM";

  let map: Record<string, string> = {};
  if (utc) {
    map = {
      DDD: days[date.getUTCDay()]!,
      DD: pad(date.getUTCDate()),
      D: date.getUTCDate().toString(),
      MMM: months[date.getUTCMonth()]!,
      MM: pad(date.getUTCMonth() + 1),
      YY: date.getUTCFullYear().toString().slice(-2),
      YYYY: date.getUTCFullYear().toString(),
      HH: pad(date.getUTCHours()),
      hh: pad(hours12),
      mm: pad(date.getUTCMinutes()),
      ss: pad(date.getUTCSeconds()),
      A: ampm,
      a: ampm.toLowerCase(),
    };
  } else {
    map = {
      DDD: days[date.getDay()]!,
      DD: pad(date.getDate()),
      D: date.getDate().toString(),
      MMM: months[date.getMonth()]!,
      MM: pad(date.getMonth() + 1),
      YY: date.getFullYear().toString().slice(-2),
      YYYY: date.getFullYear().toString(),
      HH: pad(date.getHours()),
      hh: pad(hours12),
      mm: pad(date.getMinutes()),
      ss: pad(date.getSeconds()),
      A: ampm,
      a: ampm.toLowerCase(),
    };
  }

  return format.replace(/DDD|YYYY|YY|MMM|MM|DD|HH|hh|mm|ss|A|a/g, (match) => map[match]!);
};

export const toTitleCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const capitalizeFirstLetter = (str?: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

export const PAGES_WITHOUT_NAVIGATION_BUTTON = ["/seats"];
export const isPageNavigationHidden = (pathname: string) =>
  PAGES_WITHOUT_NAVIGATION_BUTTON.includes(pathname);

export const CHECKIN_PAGES = ["/passengers", "/additional-information", "/boarding-pass"];

/**
 * Converts a currency string to a number by removing all non-numeric characters.
 * @param {string | number} value - The currency value to parse (e.g., "$15,850.00" or 15850)
 * @returns {number | null} The numeric value, or null if parsing fails
 */
export const parseCurrency = (value: string | number): number | null => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return null;

  const cleaned = value.replace(/[^0-9.-]+/g, "");
  const number = Number(cleaned);

  return Number.isFinite(number) ? number : null;
};

const YUNO_DOCUMENT_TYPE_MAPPING: Record<string, Record<string, string>> = {
  AR: { DNI: "DNI", N: "CUIL", P: "PAS" },
  BR: { DNI: "CPF", N: "CPF", P: "PAS" },
  CL: { DNI: "RUN", N: "RUT", P: "PAS" },
  CO: { DNI: "CC", N: "NIT", P: "PAS" },
  PE: { DNI: "DNI", N: "RUC", P: "PAS" },
  EC: { DNI: "CI", N: "RUC", P: "PAS" },
  UY: { DNI: "CI", N: "RUC", P: "PAS" },
  PY: { DNI: "CIC", N: "RUC", P: "PAS" },
  US: { DNI: "SSN", N: "SSN", P: "PAS" },
};

export const mapYunoDocumentType = (
  documentType: string | null | undefined,
  culture: string | null | undefined,
): string | null => {
  if (!documentType) {
    return null;
  }

  const cultureCode = culture?.split("-").pop()?.toUpperCase();

  const mapping = YUNO_DOCUMENT_TYPE_MAPPING[cultureCode ?? ""];

  // Culture is not supported → keep original Navitaire code
  if (!mapping) {
    return documentType;
  }

  return mapping[documentType] ?? documentType;
};

/**
 * Formats a date string using localized short day and month labels from CommonInfo
 * @param dateString - The date string to format
 * @param commonInfoContent - The CommonInfo object containing localized labels
 * @returns Formatted date string (e.g., "Sun Jan 15, 2025")
 */
export const formatLocalizedDate = (dateString: string, commonInfoContent: CommonInfo): string => {
  const date = new Date(dateString);
  const dayOfWeek = date.getDay();
  const month = date.getMonth();
  const day = date.getDate();
  const year = date.getFullYear();

  const dayShortLabels = [
    commonInfoContent.sundayShort,
    commonInfoContent.mondayShort,
    commonInfoContent.tuesdayShort,
    commonInfoContent.wednesdayShort,
    commonInfoContent.thursdayShort,
    commonInfoContent.fridayShort,
    commonInfoContent.saturdayShort,
  ];

  const monthShortLabels = [
    commonInfoContent.januaryShort,
    commonInfoContent.februaryShort,
    commonInfoContent.marchShort,
    commonInfoContent.aprilShort,
    commonInfoContent.mayShort,
    commonInfoContent.juneShort,
    commonInfoContent.julyShort,
    commonInfoContent.augustShort,
    commonInfoContent.septemberShort,
    commonInfoContent.octoberShort,
    commonInfoContent.novemberShort,
    commonInfoContent.decemberShort,
  ];

  return `${dayShortLabels[dayOfWeek]} ${monthShortLabels[month]} ${day}, ${year}`;
};
